import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Header from '../components/layout/Header';
import ConfirmModal from '../components/common/ConfirmModal';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';
import { getTasks, createTask, updateTask, deleteTask, getUsers, getCases } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { Plus, Circle, CheckCircle2, Trash2, User, Users, Clock, AlertCircle, ChevronDown, ChevronUp, Lock, Bell, X as XIcon, Search, Check as CheckIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const STATUSES   = ['pending', 'in_progress', 'review', 'completed'];

const TASK_TYPES = [
  'Administrative', 'Client Communication', 'Court Filing',
  'Deadline', 'Discovery', 'Draft/Revise', 'Follow-Up',
  'Hearing Prep', 'Investigation', 'Medical Records',
  'Negotiation', 'Research', 'Settlement', 'Other',
];

const REMINDER_UNITS = ['Minutes', 'Hours', 'Days', 'Weeks'];

const PRIORITY_COLOR = {
  low:    'var(--text-muted)',
  normal: '#3b82f6',
  high:   '#f59e0b',
  urgent: '#ef4444',
};

const STATUS_LABEL = {
  pending:     'Pending',
  in_progress: 'In Progress',
  review:      'Review',
  completed:   'Completed',
};

const EMPTY_FORM = {
  title: '', description: '', priority: 'normal',
  assigned_to: '', due_date: '', case_ref: '',
  task_type: '', time_estimate: '', is_private: false,
  reminder: null,
};

export default function TasksPage() {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?._id || currentUser?.id;

  const [tasks,        setTasks]        = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate,   setShowCreate]   = useState(false);
  const [users,        setUsers]        = useState([]);
  const [cases,        setCases]        = useState([]);
  const [matterSearch, setMatterSearch] = useState('');
  const [matterOpen,   setMatterOpen]   = useState(false);
  const [matterLabel,  setMatterLabel]  = useState('');
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [myExpanded,   setMyExpanded]   = useState(true);
  const [othExpanded,  setOthExpanded]  = useState(true);

  useEffect(() => { loadTasks(); }, [statusFilter]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const result = await getTasks(params);
      setTasks(result.tasks || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCreate = async () => {
    try {
      const [uRes, cRes] = await Promise.all([getUsers(), getCases({ limit: 200 })]);
      const uList = Array.isArray(uRes) ? uRes : (uRes?.users || []);
      setUsers(Array.isArray(uList) ? uList : []);
      const cList = Array.isArray(cRes) ? cRes : (cRes?.cases || []);
      setCases(Array.isArray(cList) ? cList : []);
    } catch { setUsers([]); setCases([]); }
    setForm(EMPTY_FORM);
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Task name is required'); return; }
    try {
      await createTask({
        ...form,
        reminder: form.reminder && form.reminder.amount ? form.reminder : null,
      });
      toast.success('Task created');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setMatterSearch(''); setMatterOpen(false); setMatterLabel('');
      loadTasks();
    } catch { toast.error('Failed to create task'); }
  };

  const toggleComplete = async (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await updateTask(task.id, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = (task) => setConfirmDelete(task);

  const doDeleteTask = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTask(confirmDelete.id);
      setTasks(prev => prev.filter(t => t.id !== confirmDelete.id));
      toast.success('Task deleted');
      setConfirmDelete(null);
    } catch { toast.error('Failed to delete'); }
  };

  const isOverdue = (task) =>
    task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  // ── Split into buckets ──────────────────────────────────────────────────
  const { myTasks, delegatedTasks } = useMemo(() => {
    const filtered = statusFilter ? tasks.filter(t => t.status === statusFilter) : tasks;
    const my = [], delegated = [];
    filtered.forEach(t => {
      const assignedId  = t.assigned?.id  || t.assigned_to;
      const createdById = t.createdBy?.id || t.created_by;
      const assignedToMe    = assignedId  === currentUserId;
      const createdByMe     = createdById === currentUserId;
      const assignedToOther = assignedId  && assignedId !== currentUserId;

      if (assignedToMe || createdByMe) {
        my.push(t);
      } else if (assignedToOther) {
        delegated.push(t);
      }
    });
    return { myTasks: my, delegatedTasks: delegated };
  }, [tasks, statusFilter, currentUserId]);

  // ── Task row ────────────────────────────────────────────────────────────
  const TaskRow = ({ task, isDelegated }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '11px 16px', borderBottom: '1px solid var(--zeus-border)',
      opacity: task.status === 'completed' ? 0.55 : 1,
      background: isOverdue(task) ? 'rgba(239,68,68,0.03)' : 'transparent',
    }}>
      <button onClick={() => toggleComplete(task)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
          color: task.status === 'completed' ? '#22c55e' : 'var(--text-muted)' }}>
        {task.status === 'completed' ? <CheckCircle2 size={18} /> : <Circle size={18} />}
      </button>

      <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: PRIORITY_COLOR[task.priority] || 'var(--text-muted)' }} title={`${task.priority} priority`} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: '0.88rem', color: 'var(--text-primary)',
          textDecoration: task.status === 'completed' ? 'line-through' : 'none',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.title}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '1px 7px',
            background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
            {STATUS_LABEL[task.status] || task.status}
          </span>
          {task.due_date && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.7rem',
              color: isOverdue(task) ? '#ef4444' : 'var(--text-muted)' }}>
              {isOverdue(task) && <AlertCircle size={10} />}
              <Clock size={10} /> {formatDate(task.due_date)}
            </span>
          )}
          {/* My Tasks: show if assigned to someone else */}
          {!isDelegated && task.assigned?.first_name && task.assigned?.id !== currentUserId && (
            <span style={{ fontSize: '0.7rem', color: 'var(--zeus-gold)', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <User size={10} /> You → {task.assigned.first_name} {task.assigned.last_name}
            </span>
          )}
          {/* Delegated: show assignee name */}
          {isDelegated && task.assigned?.first_name && (
            <span style={{ fontSize: '0.7rem', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <User size={10} /> {task.assigned.first_name} {task.assigned.last_name}
            </span>
          )}
        </div>
      </div>

      <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(task)}
        style={{ color: 'var(--status-red)', padding: '4px', flexShrink: 0 }} title="Delete">
        <Trash2 size={14} />
      </button>
    </div>
  );

  // ── Card wrapper ────────────────────────────────────────────────────────
  const TaskCard = ({ title, icon, cardTasks, expanded, onToggle, isDelegated, emptyText, accentColor }) => {
    const open     = cardTasks.filter(t => t.status !== 'completed').length;
    const done     = cardTasks.filter(t => t.status === 'completed').length;
    const overdue  = cardTasks.filter(t => isOverdue(t)).length;
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden', flex: 1, minWidth: '280px' }}>
        {/* Header */}
        <div onClick={onToggle} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 18px', cursor: 'pointer', userSelect: 'none',
          borderBottom: expanded ? '1px solid var(--zeus-border)' : 'none',
          background: 'var(--zeus-navy-light)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ color: accentColor }}>{icon}</div>
            <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>{title}</span>
            {open > 0 && (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                background: `${accentColor}28`, color: accentColor, borderRadius: '10px' }}>
                {open} open
              </span>
            )}
            {overdue > 0 && (
              <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: '10px' }}>
                {overdue} overdue
              </span>
            )}
            {done > 0 && (
              <span style={{ fontSize: '0.68rem', padding: '2px 8px',
                background: 'rgba(34,197,94,0.08)', color: '#22c55e', borderRadius: '10px' }}>
                {done} done
              </span>
            )}
          </div>
          {expanded ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} />
                    : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
        </div>

        {/* Body */}
        {expanded && (
          cardTasks.length > 0
            ? cardTasks.map(t => <TaskRow key={t.id} task={t} isDelegated={isDelegated} />)
            : <div style={{ padding: '30px 18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                {emptyText}
              </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Header title="Tasks">
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Task
        </button>
      </Header>

      <div className="app-content">
        {/* Filter bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          {['', ...STATUSES].map(s => (
            <button key={s} className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setStatusFilter(s)}>
              {s ? STATUS_LABEL[s] : 'All'}
            </button>
          ))}
          <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginLeft: 'auto' }}>
            {tasks.length} task{tasks.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <div className="tasks-cards-view" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TaskCard
              title="My Tasks"
              icon={<User size={16} />}
              cardTasks={myTasks}
              expanded={myExpanded}
              onToggle={() => setMyExpanded(p => !p)}
              isDelegated={false}
              emptyText="No tasks assigned to or created by you."
              accentColor="var(--zeus-gold)"
            />
            <TaskCard
              title="Delegated Tasks"
              icon={<Users size={16} />}
              cardTasks={delegatedTasks}
              expanded={othExpanded}
              onToggle={() => setOthExpanded(p => !p)}
              isDelegated={true}
              emptyText="No tasks assigned to other team members."
              accentColor="#3b82f6"
            />
          </div>
        )}
      </div>

      {/* ── New Task Modal ── */}
      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setMatterSearch(''); setMatterOpen(false); setMatterLabel(''); }} title="New task"  wide>
        <div style={{ padding: '0 2px' }}>

          {/* Section label */}
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: 14 }}>Details</div>

          {/* Row 1: Name + Priority */}
          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Name *</label>
              <input className="form-input" value={form.title} placeholder="Task name"
                onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={3} value={form.description}
              placeholder="Add details about this task..."
              onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>

          {/* Row 2: Assign To + Private task */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 12 }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Assign to</label>
              <select className="form-select" value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Me</option>
                {users.map(u => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.first_name} {u.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flexShrink: 0, paddingBottom: 2 }}>
              <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Private task</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <div
                  onClick={() => setForm({ ...form, is_private: !form.is_private })}
                  style={{
                    width: 42, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
                    background: form.is_private ? 'var(--zeus-gold)' : 'var(--zeus-border)',
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                  <div style={{
                    position: 'absolute', top: 2, borderRadius: '50%',
                    width: 20, height: 20, background: '#fff',
                    transition: 'left 0.2s',
                    left: form.is_private ? 20 : 2,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  }} />
                </div>
              </label>
            </div>
          </div>

          {/* Task Type */}
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Task type</label>
            <select className="form-select" value={form.task_type}
              onChange={e => setForm({ ...form, task_type: e.target.value })}>
              <option value="">— Select type —</option>
              {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Row 3: Status + Time Estimate */}
          <div className="form-row" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status || 'pending'}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Time estimate</label>
              <input className="form-input" value={form.time_estimate} placeholder="e.g. 1h 30m"
                onChange={e => setForm({ ...form, time_estimate: e.target.value })} />
            </div>
          </div>

          {/* Matter — searchable combobox */}
          {(() => {
            const q = matterSearch.trim().toLowerCase();
            const filteredCases = q.length < 1 ? cases : cases.filter(c => {
              const name    = (c.client_name   || '').toLowerCase();
              const cnum    = (c.case_number   || '').toLowerCase();
              const claim   = (c.claim_number  || '').toLowerCase();
              const doi     = (c.date_of_incident || '').toLowerCase();
              const dob     = (c.date_of_birth || '').toLowerCase();
              return name.includes(q) || cnum.includes(q) || claim.includes(q) ||
                     doi.includes(q) || dob.includes(q);
            });

            const selectCase = (c) => {
              const id  = c._id || c.id;
              const lbl = (c.case_number || id) + (c.client_name ? ' — ' + c.client_name : '');
              setForm(prev => ({ ...prev, case_ref: id }));
              setMatterLabel(lbl);
              setMatterSearch('');
              setMatterOpen(false);
            };

            const clearCase = () => {
              setForm(prev => ({ ...prev, case_ref: '' }));
              setMatterLabel('');
              setMatterSearch('');
              setMatterOpen(false);
            };

            return (
              <div className="form-group" style={{ marginBottom: 12, position: 'relative' }}>
                <label className="form-label">Matter</label>

                {/* Selected case chip */}
                {form.case_ref && !matterOpen ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--zeus-gold)', background: 'rgba(212,175,55,0.06)',
                    fontSize: '0.85rem', color: 'var(--text-primary)',
                  }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {matterLabel}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      <button type="button"
                        onClick={() => { setMatterOpen(true); setTimeout(() => document.getElementById('matter-search-input') && document.getElementById('matter-search-input').focus(), 50); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--zeus-gold)', fontSize: '0.72rem', padding: '0 4px', fontWeight: 600 }}>
                        Change
                      </button>
                      <button type="button" onClick={clearCase}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2 }}>
                        <XIcon size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Search input */
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                      border: '1px solid ' + (matterOpen ? 'var(--zeus-gold)' : 'var(--zeus-border)'),
                      background: 'var(--zeus-navy-light)', transition: 'border-color 0.15s',
                    }}>
                      <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <input
                        id="matter-search-input"
                        type="text"
                        value={matterSearch}
                        placeholder="Search by client name, case #, claim #, or date..."
                        autoComplete="off"
                        onChange={e => { setMatterSearch(e.target.value); setMatterOpen(true); }}
                        onFocus={() => setMatterOpen(true)}
                        style={{
                          flex: 1, background: 'none', border: 'none', outline: 'none',
                          fontSize: '0.85rem', color: 'var(--text-primary)',
                          fontFamily: 'var(--font-body)',
                        }}
                      />
                      {matterSearch && (
                        <button type="button" onClick={() => { setMatterSearch(''); setMatterOpen(false); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}>
                          <XIcon size={12} />
                        </button>
                      )}
                    </div>

                    {/* Dropdown */}
                    {matterOpen && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                        background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)',
                        borderTop: 'none', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                        maxHeight: 240, overflowY: 'auto',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      }}>
                        {/* No matter option */}
                        <div
                          onClick={clearCase}
                          style={{
                            padding: '9px 12px', cursor: 'pointer', fontSize: '0.82rem',
                            color: 'var(--text-muted)', borderBottom: '1px solid var(--zeus-border)',
                            display: 'flex', alignItems: 'center', gap: 8,
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--zeus-navy-light)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <XIcon size={12} /> No matter
                        </div>

                        {filteredCases.length === 0 ? (
                          <div style={{ padding: '12px 14px', fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                            No cases found
                          </div>
                        ) : filteredCases.slice(0, 50).map(c => {
                          const cid = c._id || c.id;
                          const isSelected = form.case_ref === cid;
                          return (
                            <div key={cid} onClick={() => selectCase(c)}
                              style={{
                                padding: '9px 12px', cursor: 'pointer',
                                background: isSelected ? 'rgba(212,175,55,0.08)' : 'transparent',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                display: 'flex', alignItems: 'center', gap: 10,
                              }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--zeus-navy-light)'; }}
                              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                              {isSelected && <CheckIcon size={12} style={{ color: 'var(--zeus-gold)', flexShrink: 0 }} />}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ color: 'var(--zeus-gold)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>{c.case_number || cid}</span>
                                  <span>{c.client_name || 'Unknown'}</span>
                                </div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                  {c.claim_number && <span>Claim: {c.claim_number}</span>}
                                  {c.date_of_incident && <span>DOI: {c.date_of_incident.slice(0,10)}</span>}
                                  {c.case_type && <span>{c.case_type}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Click-outside dismiss */}
                {matterOpen && (
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                    onClick={() => { setMatterOpen(false); setMatterSearch(''); }}
                  />
                )}
              </div>
            );
          })()}

          {/* Due Date */}
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Due date</label>
            <input className="form-input" type="date" value={form.due_date}
              onChange={e => setForm({ ...form, due_date: e.target.value })} />
          </div>

          {/* Reminder */}
          <div className="form-group" style={{ marginBottom: 18 }}>
            <label className="form-label">Reminder</label>
            {form.reminder ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* Who */}
                <select className="form-select" style={{ flex: '1 1 120px', minWidth: 100 }}
                  value={form.reminder.who || 'me'}
                  onChange={e => setForm({ ...form, reminder: { ...form.reminder, who: e.target.value } })}>
                  <option value="me">Me (via email)</option>
                  <option value="assignee">Assignee</option>
                  <option value="everyone">Everyone</option>
                </select>
                {/* Amount */}
                <input className="form-input" type="number" min={1}
                  style={{ width: 64, flexShrink: 0 }}
                  value={form.reminder.amount || ''}
                  onChange={e => setForm({ ...form, reminder: { ...form.reminder, amount: e.target.value } })}
                  placeholder="10" />
                {/* Unit */}
                <select className="form-select" style={{ flex: '1 1 100px', minWidth: 90 }}
                  value={form.reminder.unit || 'Minutes'}
                  onChange={e => setForm({ ...form, reminder: { ...form.reminder, unit: e.target.value } })}>
                  {REMINDER_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                {/* Direction */}
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', flexShrink: 0 }}>Before</span>
                {/* Remove */}
                <button type="button"
                  onClick={() => setForm({ ...form, reminder: null })}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--status-red)', padding: 2, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  <XIcon size={16} />
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => setForm({ ...form, reminder: { who: 'me', amount: '10', unit: 'Minutes', direction: 'before' } })}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bell size={13} /> Add reminder
              </button>
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
              onClick={handleCreate}>
              Save task
            </button>
            <button type="button" className="btn btn-ghost"
              onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDeleteTask}
        title="Delete Task"
        message="This task will be permanently removed."
        detail={confirmDelete && (
          <div>
            <div style={{ fontWeight: 700 }}>{confirmDelete.title}</div>
            {confirmDelete.due_date && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
                Due: {new Date(confirmDelete.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
        )}
        confirmLabel="Delete Task"
      />
    </>
  );
}
